// PowerSync local-first dispatch for `createFieldAddedVariant`.
//
// Flow:
//   1. Generate a UUID v4 client-side.
//   2. INSERT into local `asset_types` with `is_field_added=1,
//      requires_review=1, code=null, rejected_at=null,
//      display_order=0` (server reconciles display_order on sync).
//   3. JOIN-readback against `asset_sub_systems` + `asset_systems` to
//      assemble a `FieldAddedAssetType` shape that matches the REST
//      response (10-api.md §5.8).
//   4. Emit a `field-added-variant-created` event so subscribers (the
//      review queue, UI counters) see the new variant immediately.
//
// The connector picks up the SDK CRUD entry and POSTs to
// `/capture/field-additions` with the same client-supplied id. Backend
// honours the id (idempotent retry on same id, 409 already_exists with
// authoritative on conflict at different id; connector swallows + DELETEs
// the local orphan in that case).
//
// Matches the createItem pattern. The two helpers share nothing
// runtime — they just live behind the same conceptual contract — so
// they're separate files.

import type {
  CaptureMutation,
  CaptureMutationResult,
} from './capture-client.types'
import { eventForResult } from './capture-client-events'
import type { CaptureEventBus } from './capture-events'

import type { PowerSyncDb } from './capture-client-powersync'

/** Wrapped for testability: the caller can replace this with a fixed
 * UUID generator. The runtime path uses `crypto.randomUUID()` which is
 * cryptographically-strong (not just an `Math.random()` substring). */
export function generateUuidV4(): string {
  return crypto.randomUUID()
}

export async function dispatchCreateFieldAddedVariant(
  mutation: Extract<CaptureMutation, { kind: 'createFieldAddedVariant' }>,
  db: PowerSyncDb,
  bus: CaptureEventBus,
): Promise<CaptureMutationResult> {
  const id = generateUuidV4()
  const nowIso = new Date().toISOString()

  // Optimistic local insert. The server's `display_order = MAX + 1`
  // reconciliation will overwrite the 0 default when the synced row
  // arrives. `code` stays null (field-added variants have no CCL code
  // until admin review per PRD §6.5).
  await db.execute(
    `INSERT INTO asset_types (
       id,
       sub_system_id,
       name,
       code,
       unit_of_measure,
       grouping_convention,
       is_field_added,
       requires_review,
       rejected_at,
       display_order,
       created_at,
       updated_at
     ) VALUES (?, ?, ?, ?, ?, ?, 1, 1, NULL, 0, ?, ?)`,
    [
      id,
      mutation.subSystemId,
      mutation.name,
      null,
      mutation.unitOfMeasure,
      mutation.groupingConvention,
      nowIso,
      nowIso,
    ],
  )

  // JOIN against the synced sub-system + system tables so the response
  // DTO matches the REST shape (subSystemName + systemName denormalised).
  const rows = await db.getAll<Record<string, unknown>>(
    `SELECT
       at.id                     AS id,
       at.sub_system_id          AS subSystemId,
       ass.id                    AS systemId,
       ass.name                  AS systemName,
       asub.name                 AS subSystemName,
       at.name                   AS name,
       at.code                   AS code,
       at.unit_of_measure        AS unitOfMeasure,
       at.grouping_convention    AS groupingConvention,
       at.is_field_added         AS isFieldAddedRaw,
       at.requires_review        AS requiresReviewRaw,
       at.display_order          AS displayOrder,
       at.created_at             AS createdAt
     FROM asset_types at
     LEFT JOIN asset_sub_systems asub ON asub.id = at.sub_system_id
     LEFT JOIN asset_systems ass      ON ass.id  = asub.system_id
     WHERE at.id = ?
     LIMIT 1`,
    [id],
  )

  // Defensive fallback: if the JOIN didn't return (shouldn't happen —
  // the INSERT just ran), synthesise a minimal row from the mutation
  // so downstream subscribers don't crash on undefined.
  const raw = rows[0]
  const assetType = raw
    ? {
        id: String(raw['id'] ?? id),
        subSystemId: String(raw['subSystemId'] ?? mutation.subSystemId),
        systemId: String(raw['systemId'] ?? ''),
        systemName: String(raw['systemName'] ?? ''),
        subSystemName: String(raw['subSystemName'] ?? ''),
        name: String(raw['name'] ?? mutation.name),
        code:
          raw['code'] === null || raw['code'] === undefined
            ? null
            : String(raw['code']),
        unitOfMeasure: String(raw['unitOfMeasure'] ?? mutation.unitOfMeasure),
        groupingConvention: String(
          raw['groupingConvention'] ?? mutation.groupingConvention,
        ),
        // Forced to true on the response per the FieldAddedAssetType
        // contract — every successful dispatch reflects a field-added
        // row by definition.
        isFieldAdded: true as const,
        requiresReview: true as const,
        displayOrder: Number(raw['displayOrder'] ?? 0),
        createdAt: String(raw['createdAt'] ?? new Date().toISOString()),
      }
    : {
        id,
        subSystemId: mutation.subSystemId,
        systemId: '',
        systemName: '',
        subSystemName: '',
        name: mutation.name,
        code: null,
        unitOfMeasure: mutation.unitOfMeasure,
        groupingConvention: mutation.groupingConvention,
        isFieldAdded: true as const,
        requiresReview: true as const,
        displayOrder: 0,
        createdAt: new Date().toISOString(),
      }

  const result: CaptureMutationResult = {
    kind: 'createFieldAddedVariant',
    assetType,
  }
  bus.emit(eventForResult(mutation, result))
  return result
}
